
"use client"

import { useState, useEffect, useMemo, useCallback } from "react"
import { useDispatch, useSelector } from 'react-redux';
import { Button } from "../components/ui/button"
import { Input } from "../components/ui/input"
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card"
import promosTransactionService from '../apis/promos'
import { employeeManagementService } from '../apis/employeeManagement';
import { toast } from "react-hot-toast"
import moment, { currentBusinessDate, defaultDateRange, startOfDay, endOfDay, isDateTimeInRange, parseAppDateTime } from "../utils/timezone"
import { PromosAddModal } from "../components/promos-add-modal"
import { PromosEditModal } from "../components/promos-edit-modal"
import { PromosDeleteModal } from "../components/promos-delete-modal"
import { PromosTransactionTable } from "../components/promos-transaction-table"
import DateRangePicker from '../components/ui/date-range-picker'
import { Calendar, Pencil } from "lucide-react"
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import AdminEditModal from "../components/AdminEditModal"
import { useAuth } from "../context/AuthContext"
import { setPromosTransactionDetails } from '../Store/actions/transactionDetails'
import { base64Logo } from '../constants/pdfLogo'

export default function PromosTransactions() {
  const dispatch = useDispatch();
  const { currentUser } = useAuth();
  const transactionDetails = useSelector(state => state.transactionDetails.promosTransactionDetails);
  
  const [user, setUser] = useState(null);
  const [transactions, setTransactions] = useState([])
  const [currentBalance, setCurrentBalance] = useState(0) // Store current balance separately
  const [allemployees, setallemployees] = useState([]);
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [dateRange, setDateRange] = useState(defaultDateRange);

  const [refreshFlag, setRefreshFlag] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [currentTransaction, setCurrentTransaction] = useState(null)
  const [mostRecentId, setMostRecentId] = useState(null)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [entriesPerPage, setEntriesPerPage] = useState(10)
  const [transactionDetailsUpdated, setTransactionDetailsUpdated] = useState(false);
  const [isAdminEditModalOpen, setIsAdminEditModalOpen] = useState(false);

  const isCashier = (currentUser?.role || user?.role || "").trim() === "Cashier";
  const currentMonthStart = useMemo(() => moment().startOf("month").toDate(), []);

  // Load promo catalog from server (shared across all staff)
  useEffect(() => {
    const loadPromoOptions = async () => {
      try {
        const response = await promosTransactionService.getPromoOptions();
        if (response.success) {
          const options = response.data?.options || response.data?.data?.options;
          if (Array.isArray(options) && options.length > 0) {
            dispatch(setPromosTransactionDetails(options));
            localStorage.setItem('transactionDetails', JSON.stringify(options));
            return;
          }
        }
      } catch (error) {
        console.error('Failed to load promo options from server:', error);
      }

      // Fallback to local cache / defaults
      const savedDetails = localStorage.getItem('transactionDetails');
      if (savedDetails) {
        try {
          dispatch(setPromosTransactionDetails(JSON.parse(savedDetails)));
        } catch (parseError) {
          console.error('Failed to parse cached promo options:', parseError);
        }
      }
    };

    loadPromoOptions();
  }, [dispatch]);

  // Cashiers default to current month only (cannot view past months)
  useEffect(() => {
    if (!isCashier) return;
    setDateRange({
      start: startOfDay(currentMonthStart),
      end: endOfDay(currentBusinessDate()),
    });
  }, [isCashier, currentMonthStart]);

  // Initialize user from localStorage
  useEffect(() => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        setUser(JSON.parse(userData));
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
      setUser(null);
    }
  }, []);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await employeeManagementService.getAllEmployees({ limit: 1000000 });
        if (response.success) {
          setallemployees(response?.data?.body?.data?.employees);
        }
      } catch (error) {
        console.error('Error fetching employees:', error);
      }
    };

    fetchEmployees();
  }, []);

  // Helper function to safely parse numeric values
  const parseNumericValue = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  };

  const formatCurrency = (value) => {
    return parseFloat(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const dateRangeSummary = useMemo(() => {
    const effectiveStart =
      isCashier && moment(dateRange.start).isBefore(currentMonthStart, "day")
        ? currentMonthStart
        : dateRange.start;

    const filteredByDateRange = transactions.filter((transaction) => {
      const parsed = parseAppDateTime(transaction.date);
      if (!parsed) return false;
      if (isCashier && parsed.isBefore(currentMonthStart, "day")) return false;
      return isDateTimeInRange(transaction.date, effectiveStart, dateRange.end);
    });

    const deposit = filteredByDateRange.reduce((sum, tx) =>
      tx.transaction_type === "deposit" ? sum + parseNumericValue(tx.transaction_amount) : sum, 0);

    const withdraw = filteredByDateRange.reduce((sum, tx) =>
      tx.transaction_type === "withdraw" ? sum + parseNumericValue(tx.transaction_amount) : sum, 0);

    return {
      deposit,
      withdraw,
      balance: currentBalance
    };
  }, [transactions, dateRange, currentBalance, isCashier, currentMonthStart]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      if (user.role === "Cashier") {
        const verificationRes = await promosTransactionService.checkVerificationStatus();
        if (verificationRes.success) {
          if (verificationRes.data.status === "unverified") {
            setLoading(false);
            return;
          }
        }
      }

      const [balanceRes, transactionsRes] = await Promise.all([
        promosTransactionService.getCurrentBalance(),
        promosTransactionService.getAllTransactions()
      ]);

      if (balanceRes.success && transactionsRes.success) {
        const rawBalance = parseNumericValue(balanceRes.data.balance);
        const roundedBalance = Math.max(0, Math.round(rawBalance));

        const transactionData = transactionsRes.data || [];

        // Sort transactions by date descending
        const sortedTransactions = [...transactionData].sort((a, b) =>
          (parseAppDateTime(b.date)?.valueOf() ?? 0) - (parseAppDateTime(a.date)?.valueOf() ?? 0)
        );

        setCurrentBalance(roundedBalance);
        setTransactions(sortedTransactions);

        // Set most recent transaction ID for deletion validation
        if (sortedTransactions.length > 0) {
          setMostRecentId(sortedTransactions[0].id);
        }
      } else {
        toast.error("Failed to load data");
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [user]); // Removed verificationStatus from dependencies

  useEffect(() => {
    if (!user) return;
    fetchData();
  }, [user, refreshFlag, fetchData]); // Added fetchData to dependencies

  const openAddModal = () => setIsAddModalOpen(true);
  const openEditModal = (transaction) => {
    setCurrentTransaction(transaction);
    setIsEditModalOpen(true);
  };
  const openDeleteModal = (transaction) => {
    setCurrentTransaction(transaction);
    setIsDeleteModalOpen(true);
  };

  const handleAddTransaction = async (formData) => {
    try {
      const response = await promosTransactionService.addTransaction(formData);

      if (response.success) {
        toast.success(response.message || "Transaction added successfully");
        const newTransaction = response.data;

        // Add to beginning of transactions array
        setTransactions(prev => {
          const newTransactions = [newTransaction, ...prev];

          // Update most recent ID
          if (newTransactions.length > 0) {
            setMostRecentId(newTransactions[0].id);
          }

          return newTransactions;
        });

        // Immediately update current balance
        setCurrentBalance(parseNumericValue(newTransaction.after_balance));

        setRefreshFlag(prev => !prev);
        setCurrentPage(1);
        return true;
      } else {
        toast.error(response.error || "Transaction failed");
        return false;
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error("Transaction failed");
      return false;
    }
  };

  const handleUpdateTransaction = async (id, updatedData) => {
    try {
      const response = await promosTransactionService.updateTransaction(id, updatedData);

      if (response.success) {
        toast.success(response.message || "Transaction updated successfully");

        setTransactions(prev => prev.map(tx =>
          tx.id === id
            ? {
                ...tx,
                ...(response.data && typeof response.data === "object" ? response.data : {}),
                date: updatedData.date || tx.date,
                player_name: updatedData.playerName ?? updatedData.player_name ?? tx.player_name,
                notes: updatedData.notes ?? tx.notes,
              }
            : tx
        ));

        // Refresh current balance
        const balanceRes = await promosTransactionService.getCurrentBalance();
        if (balanceRes.success) {
          setCurrentBalance(parseNumericValue(balanceRes.data.balance));
        }
        setRefreshFlag(prev => !prev);
        return true;
      } else {
        toast.error(response.error || "Update failed");
        return false;
      }
    } catch (error) {
      console.error('Edit error:', error);
      toast.error("Update failed");
      return false;
    }
  };

  const handleDeleteTransaction = async (id) => {
    try {
      const response = await promosTransactionService.deleteTransaction(id);

      if (response.success) {
        toast.success(response.message || "Transaction deleted successfully");

        // Update local state
        const deletedTx = transactions.find(tx => tx.id === id);
        if (deletedTx) {
          const isDeposit = deletedTx.transaction_type === "deposit";
          const amount = parseNumericValue(deletedTx.transaction_amount);

          setTransactions(prev => {
            const newTransactions = prev.filter(tx => tx.id !== id);

            // Update most recent ID if needed
            if (id === mostRecentId && newTransactions.length > 0) {
              setMostRecentId(newTransactions[0].id);
            }
            setRefreshFlag(prev => !prev);
            return newTransactions;
          });

          // Update current balance
          setCurrentBalance(prev =>
            isDeposit ? prev - amount : prev + amount
          );
        }

        return true;
      } else {
        toast.error(response.error || "Deletion failed");
        return false;
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error("Deletion failed");
      return false;
    }
  };

  const handleDateRangeApply = (startDate, endDate) => {
    let nextStart = startOfDay(startDate);
    let nextEnd = endOfDay(endDate);

    // Cashiers cannot view past-month data
    if (isCashier) {
      if (moment(nextStart).isBefore(currentMonthStart, "day")) {
        nextStart = startOfDay(currentMonthStart);
        toast.error("Cashiers can only view current month data");
      }
      if (moment(nextEnd).isBefore(currentMonthStart, "day")) {
        nextEnd = endOfDay(currentBusinessDate());
      }
    }

    setDateRange({
      start: nextStart,
      end: nextEnd,
    });
    setIsDatePickerOpen(false);
    setCurrentPage(1);
  };

  const formatDateRange = () => {
    return `${moment(dateRange.start).format("MM/DD/YYYY")} - ${moment(dateRange.end).format("MM/DD/YYYY")}`
  };

  // Filter transactions based on search term and date range
  const filteredTransactions = transactions
    .filter(transaction => {
      const matchesSearch = !searchTerm ||
        (transaction.player_name && transaction.player_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (transaction.notes && transaction.notes.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (transaction.added_by && transaction.added_by.toLowerCase().includes(searchTerm.toLowerCase()));

      const effectiveStart =
        isCashier && moment(dateRange.start).isBefore(currentMonthStart, "day")
          ? currentMonthStart
          : dateRange.start;

      const parsed = parseAppDateTime(transaction.date);
      const withinCashierWindow =
        !isCashier || !parsed || !parsed.isBefore(currentMonthStart, "day");

      const matchesDateRange = isDateTimeInRange(
        transaction.date,
        effectiveStart,
        dateRange.end
      );

      return matchesSearch && matchesDateRange && withinCashierWindow;
    })
    .map(transaction => ({
      ...transaction,
      isDeletable: transaction.id === mostRecentId
    }));

  if (!user) {
    return (
      <div className="p-6 bg-gray-50">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-500"></div>
        </div>
      </div>
    );
  }


  const exportPromosToPDF = () => {
    try {
      const doc = new jsPDF();

      const logoWidth = 40; // adjust width as needed
      const logoHeight = 20; // adjust height as needed
      doc.addImage(base64Logo, 'PNG', 150, 10, logoWidth, logoHeight); // x, y, width, height


      // Set document properties
      doc.setProperties({
        title: 'Promos Transactions Report',
        subject: 'Promos Transactions',
        author: 'Elite Poker Lounge',
        creator: 'Promos System'
      });

      // Add timestamp
      const now = new Date();
      const dateTime = now.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });

      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Date & Time: ${dateTime}`, 14, 15);
      doc.text(`Date Range: ${formatDateRange()}`, 14, 25);

      // Title
      doc.setFontSize(18);
      doc.setTextColor(0);
      doc.setFont(undefined, 'bold');
      doc.text('Promos Transactions Report', 105, 40, { align: 'center' });

      // Summary section
      doc.setFontSize(12);
      doc.setTextColor(0);
      doc.setFont(undefined, 'bold');
      doc.text('Summary:', 14, 50);

      doc.setFont(undefined, 'normal');
      doc.text(`Total Deposit: $${formatCurrency(dateRangeSummary.deposit)}`, 14, 60);
      doc.text(`Total Withdraw: $${formatCurrency(dateRangeSummary.withdraw)}`, 14, 70);
      doc.text(`Current Balance: $${formatCurrency(currentBalance)}`, 14, 80);

      // Promotion-wise totals (group by Promotion Details / notes)
      const promotionTotals = {};
      filteredTransactions.forEach((tx) => {
        const key = (tx.notes || '').trim() || 'Unspecified';
        if (!promotionTotals[key]) {
          promotionTotals[key] = { withdraw: 0, deposit: 0 };
        }
        const amount = parseNumericValue(tx.transaction_amount);
        if (tx.transaction_type === 'deposit') {
          promotionTotals[key].deposit += amount;
        } else if (tx.transaction_type === 'withdraw') {
          promotionTotals[key].withdraw += amount;
        }
      });

      const promotionEntries = Object.entries(promotionTotals).sort((a, b) =>
        a[0].localeCompare(b[0])
      );

      let yPos = 92;
      doc.setFont(undefined, 'bold');
      doc.setFontSize(12);
      doc.text('Promotion Wise Transaction:', 14, yPos);
      yPos += 8;

      doc.setFont(undefined, 'normal');
      doc.setFontSize(10);

      if (promotionEntries.length === 0) {
        doc.text('No promotion transactions in this range.', 14, yPos);
        yPos += 8;
      } else {
        promotionEntries.forEach(([promoName, totals], index) => {
          const line = `${index + 1}. ${promoName}: Total Withdraw = $${formatCurrency(totals.withdraw)} | Total Deposit = $${formatCurrency(totals.deposit)}`;
          const wrapped = doc.splitTextToSize(line, 180);

          if (yPos + wrapped.length * 5 > 280) {
            doc.addPage();
            yPos = 20;
          }

          doc.text(wrapped, 14, yPos);
          yPos += wrapped.length * 5 + 2;
        });
      }

      yPos += 6;

      // Prepare table data
      const tableHeaders = [
        'Date',
        'Promos Balance',
        'Transaction',
        'Promotion Details',
        'Player Name',
        'After Balance',
        'Added By'
      ];

      const tableData = filteredTransactions.map(transaction => {
        const transactionAmount = transaction.transaction_type === 'deposit'
          ? `+${formatCurrency(transaction.transaction_amount)}`
          : `-${formatCurrency(transaction.transaction_amount)}`;

        return [
          moment(transaction.date).format("MM/DD/YYYY hh:mm A"),
          `$${formatCurrency(transaction.before_balance)}`,
          transactionAmount,
          transaction.notes || 'N/A',
          transaction.player_name || 'N/A',
          `$${formatCurrency(transaction.after_balance)}`,
          transaction.added_by || 'N/A'
        ];
      });

      // Add table using autoTable function (CORRECTED USAGE)
      autoTable(doc, {
        head: [tableHeaders],
        body: tableData,
        startY: yPos,
        theme: 'grid',
        styles: {
          fontSize: 9,
          cellPadding: 3,
          textColor: [0, 0, 0],
          lineColor: [0, 0, 0],
          lineWidth: 0.1
        },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          lineWidth: 0.1,
          lineColor: [0, 0, 0]
        },
        columnStyles: {
          0: { cellWidth: 25 },
          1: { cellWidth: 25 },
          2: { cellWidth: 20 },
          3: { cellWidth: 40 },
          4: { cellWidth: 25 },
          5: { cellWidth: 25 },
          6: { cellWidth: 30 }
        },
        margin: { left: 14, right: 14 },
        tableLineColor: [0, 0, 0],
        tableLineWidth: 0.1
      });

      // Save the PDF
      const fileName = `Promos_Report_${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}.pdf`;
      doc.save(fileName);
      toast.success('PDF exported successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Error generating PDF. Please try again.');
    }
  };

  // Add this handler function
  const handleAdminEditSave = async (updatedDetails) => {
    const response = await promosTransactionService.savePromoOptions(updatedDetails);
    if (!response.success) {
      toast.error(response.error || 'Failed to save promo list');
      throw new Error(response.error || 'Failed to save promo list');
    }

    const options = response.data?.options || updatedDetails;
    dispatch(setPromosTransactionDetails(options));
    localStorage.setItem('transactionDetails', JSON.stringify(options));
    setTransactionDetailsUpdated(prev => !prev);
    toast.success('Promo list updated for all staff');
  };


  // Update the Admin/edit button click handler
  const handleAdminEditClick = () => {
    setIsAdminEditModalOpen(true);
  };

  return (
    <div className="bg-gray-50 p-6">
        {/* Header */}
        <div className="flex justify-between items-center w-full mb-8">
          <div className="text-center flex-1">
            <h1 className="text-3xl font-bold text-gray-800 tracking-tight">
              Promos Transactions
            </h1>
            <p className="text-gray-600 mt-2 text-sm">
              Manage and track promotional transactions
            </p>
          </div>
          {currentUser?.role?.trim() === "Admin" && (
            <div
              className="flex items-center gap-2 text-sm text-gray-700 hover:text-blue-600 px-3 py-1.5 rounded-md border border-gray-300 hover:border-blue-500 shadow-sm transition-colors duration-200 cursor-pointer"
              onClick={handleAdminEditClick}
            >
              <Pencil className="w-4 h-4" />
              <span className="font-medium">Admin</span>
            </div>
          )}


        </div>



        {/* Summary row: date range first, then metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
          <Card className="bg-white shadow-sm border border-gray-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wide text-center">
                Date Range
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex flex-col gap-1">
                <Button
                  variant="outline"
                  onClick={() => setIsDatePickerOpen(true)}
                  className="w-full justify-start text-left font-normal"
                >
                  <Calendar className="mr-2 h-4 w-4 shrink-0" />
                  <span className="truncate">{formatDateRange()}</span>
                </Button>
                {isCashier && (
                  <p className="text-xs text-amber-700 text-center">
                    Cashiers can only view current month data.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-sm border border-gray-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wide text-center">
                Total Deposit
                <span className="block text-xs text-gray-400 normal-case mt-1">
                  ({formatDateRange()})
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  ${formatCurrency(dateRangeSummary.deposit)}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-sm border border-gray-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wide text-center">
                Total Money Withdraw
                <span className="block text-xs text-gray-400 normal-case mt-1">
                  ({formatDateRange()})
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">
                  ${formatCurrency(dateRangeSummary.withdraw.toFixed(2))}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-sm border border-gray-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wide text-center">
                Current Balance
                <span className="block text-xs text-gray-400 normal-case mt-1">
                  (Live Balance)
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  ${formatCurrency(currentBalance < 0 ? 0 : currentBalance)}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Transaction Form */}
        <Card className="bg-white shadow-sm border border-gray-200 mb-8">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-800">
              Add New Transaction
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-center">
              <Button
                onClick={openAddModal}
                className="h-12 px-8 bg-yellow-400 hover:bg-yellow-500 text-black font-medium"
              >
                Add New Transaction
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Transaction History */}
        <Card className="bg-white shadow-sm border border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-800">
              Transaction History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Controls */}
            <div className="flex flex-col sm:flex-row justify-end items-start sm:items-center gap-3 mb-6">
              <Button
                onClick={exportPromosToPDF}
                variant="outline"
                className="bg-yellow-400 hover:bg-yellow-500 text-black"
              >
                Export to PDF
              </Button>

              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Search:
              </label>
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search transactions..."
                className="w-64"
              />
            </div>

            {/* Loading State */}
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <div className="p-6 bg-gray-50">
                  <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-500"></div>
                  </div>
                </div>
              </div>
            ) : (
              <PromosTransactionTable
                transactions={filteredTransactions}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                entriesPerPage={entriesPerPage}
                setEntriesPerPage={setEntriesPerPage}
                onEdit={openEditModal}
                onDelete={openDeleteModal}
                dateRange={dateRange}
                employeeApiService={employeeManagementService}
                employees={allemployees}
              />
            )}
          </CardContent>
        </Card>


        {/* Add Modal */}
        <PromosAddModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onAddTransaction={handleAddTransaction}
          transactionDetails={transactionDetails}
          key={transactionDetailsUpdated} // This will force re-render when details change
        />

        <AdminEditModal
          isOpen={isAdminEditModalOpen}
          onClose={() => setIsAdminEditModalOpen(false)}
          onSave={handleAdminEditSave}
        />

        {/* Edit Modal */}
        <PromosEditModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          transaction={currentTransaction}
          onUpdateTransaction={handleUpdateTransaction}
          currentUser={user}
          transactionDetails={transactionDetails}
        />

        {/* Delete Modal */}
        <PromosDeleteModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          transaction={currentTransaction}
          onDeleteTransaction={handleDeleteTransaction}
        />

      {/* Date Range Picker */}
      {isDatePickerOpen && (
        <DateRangePicker
          isOpen={isDatePickerOpen}
          onClose={() => setIsDatePickerOpen(false)}
          onApply={handleDateRangeApply}
          initialStartDate={dateRange.start}
          initialEndDate={dateRange.end}
        />
      )}
    </div>
  );
}